/**
 * Contenido (textos) del test de idoneidad de Lift v1.
 *
 * En runtime los textos vienen del EP `GET /suitability/questionnaire`, que el
 * wizard inyecta en i18next; aquí viven los tipos del blob, el accessor tipado y el
 * mapeo de opción del catálogo a opción del radio genérico.
 *
 * El blob de Lift no lleva `answerKey`: la clave de la pregunta (`q1`) ES la clave
 * de la respuesta. Tampoco lleva tipos de widget ni reglas de habilitación — los
 * textos son datos y las reglas viven en el motor y en `gating.ts`.
 */
import { useTranslation } from 'react-i18next';
import { QUESTIONNAIRE_KEY_ROOT } from 'src/suitability/engine/i18n-questionnaire';
import type { RadioOption } from 'src/suitability/engine/components/radio-question';
import type { MultiSelectOption } from 'src/suitability/engine/components/multi-select-question';

export interface LiftOption {
  /** Orden 1-based: es la respuesta que viaja al API y el índice del peso en el motor. */
  order: number;
  label: string;
}

export interface LiftQuestion {
  prompt: string;
  /**
   * Opcional porque en el catálogo lo es: S2, el porcentaje de cartera sostenible, no
   * trae `options` — viene con `suffix`.
   */
  options?: LiftOption[];
  /** Unidad del valor cuando la pregunta no es de opciones (`%` en S2). */
  suffix?: string;
}

/**
 * Un paso del catálogo. `key` y `label` los trae el blob y no los lee ningún
 * componente —las etiquetas del stepper salen de la definición—, pero se declaran
 * igual que en `QuestionnaireStep` de Wio: el tipo describe el formato de cable.
 */
export interface LiftStep {
  key: string;
  label: string;
  title?: string;
  intro?: string;
}

/** Getters tipados sobre el contenido del cuestionario inyectado en i18next. */
export const useLiftContent = () => {
  const { t } = useTranslation();

  // i18next devuelve la propia clave (un string) cuando no existe la traducción, así
  // que eso es lo que distingue "no está" de "es un objeto".
  const read = <T>(key: string): T | undefined => {
    const value = t(`${QUESTIONNAIRE_KEY_ROOT}.${key}`, {
      returnObjects: true,
    });

    return typeof value === 'string' ? undefined : (value as unknown as T);
  };

  return {
    /**
     * Si el catálogo no trae un paso o una pregunta, se devuelve su clave como texto y
     * sin opciones: el hueco se VE en pantalla en vez de tumbar el wizard con un
     * `undefined`. No debería pasar —el test de contrato de la API ata catálogo y
     * motor—, pero el contenido llega por red y es de otra compañía.
     */
    step: (key: string): LiftStep =>
      read<LiftStep>(`steps.${key}`) ?? { key, label: key, title: key },
    question: (key: string): LiftQuestion =>
      read<LiftQuestion>(`questions.${key}`) ?? { prompt: key, options: [] },
  };
};

/**
 * Opción del catálogo → opción del radio genérico, que pide `id` y `value` string.
 * El `value` es el orden, porque el orden es la respuesta.
 */
export const toRadioOptions = (
  questionKey: string,
  question: LiftQuestion,
): RadioOption[] =>
  (question.options ?? []).map((option) => ({
    id: `${questionKey}-${option.order}`,
    label: option.label,
    value: String(option.order),
  }));

/**
 * Ídem para la multiselección, donde el `id` ES la respuesta: `MultiSelectQuestion`
 * lleva un `Record<id, boolean>` y el paso lo convierte a la lista de órdenes que
 * espera el motor.
 */
export const toMultiSelectOptions = (
  question: LiftQuestion,
): MultiSelectOption[] =>
  (question.options ?? []).map((option) => ({
    id: String(option.order),
    label: option.label,
  }));
